import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase/firebaseConfig";
import {
  FiActivity,
  FiAlertTriangle,
  FiBell,
  FiBookOpen,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiUserPlus,
} from "react-icons/fi";

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return new Date(ts > 1e12 ? ts : ts * 1000);
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
    if (typeof ts.toDate === "function") return ts.toDate();
  }
  return null;
}

function tsMs(ts) {
  const d = tsToDate(ts);
  return d ? d.getTime() : 0;
}

function fmt(ts) {
  const d = tsToDate(ts);
  return d ? d.toLocaleString("vi-VN") : "—";
}

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

function getStudentName(studentMap, id, fallback = "Học viên") {
  const s = studentMap[id];
  return (
    s?.display?.name ||
    s?.name ||
    `${s?.firstName || ""} ${s?.lastName || ""}`.trim() ||
    s?.email ||
    id ||
    fallback
  );
}

function getPercent(item) {
  if (typeof item?.percent === "number") return Math.round(item.percent);
  const correct = item?.score?.correct;
  const total = item?.score?.total;
  if (typeof correct === "number" && typeof total === "number" && total > 0) {
    return Math.round((correct / total) * 100);
  }
  return null;
}

const typeMeta = {
  student_enrolled: { label: "Học viên", icon: FiUserPlus, tone: "blue" },
  student_removed: { label: "Học viên", icon: FiUserPlus, tone: "red" },
  student_status_changed: { label: "Học viên", icon: FiRefreshCw, tone: "amber" },
  attendance_updated: { label: "Điểm danh", icon: FiClipboard, tone: "emerald" },
  assignment_created: { label: "Bài tập", icon: FiBookOpen, tone: "blue" },
  assignment_updated: { label: "Bài tập", icon: FiRefreshCw, tone: "amber" },
  assignment_submitted: { label: "Nộp bài", icon: FiSend, tone: "emerald" },
  notification_created: { label: "Thông báo", icon: FiBell, tone: "purple" },
  notification_updated: { label: "Thông báo", icon: FiRefreshCw, tone: "amber" },
  notification_read: { label: "Đã đọc", icon: FiCheckCircle, tone: "emerald" },
  class_activity: { label: "Hoạt động", icon: FiActivity, tone: "slate" },
};

const toneClass = {
  blue: "border-blue-100 bg-blue-50 text-blue-700",
  emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
  amber: "border-amber-100 bg-amber-50 text-amber-700",
  red: "border-red-100 bg-red-50 text-red-700",
  purple: "border-purple-100 bg-purple-50 text-purple-700",
  slate: "border-slate-100 bg-slate-50 text-slate-700",
};

function ActivityIcon({ type, severity }) {
  const meta = typeMeta[type] || typeMeta.class_activity;
  const Icon = severity === "warning" || severity === "danger" ? FiAlertTriangle : meta.icon;
  const tone = severity === "danger" ? "red" : severity === "warning" ? "amber" : meta.tone;
  return (
    <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${toneClass[tone] || toneClass.slate}`}>
      <Icon />
    </span>
  );
}

function buildAttendanceMessage(item) {
  const students = Array.isArray(item.students) ? item.students : [];
  const counts = students.reduce(
    (acc, s) => {
      acc[s.status] = (acc[s.status] || 0) + 1;
      return acc;
    },
    { present: 0, absent: 0, late: 0, excused: 0 }
  );
  return `Đã cập nhật điểm danh ngày ${item.date || "—"}: ${counts.present || 0} có mặt, ${counts.absent || 0} vắng, ${counts.late || 0} đi muộn, ${counts.excused || 0} có phép.`;
}

export default function TabActivities({ classInfo, students = [] }) {
  const [manualLogs, setManualLogs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [histories, setHistories] = useState([]);
  const [reads, setReads] = useState([]);
  const [errors, setErrors] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const studentMap = useMemo(() => {
    const map = {};
    (students || []).forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [students]);

  useEffect(() => {
    if (!classInfo?.id) return undefined;
    setErrors([]);

    const makeSub = (name, qRef, setter) =>
      onSnapshot(
        qRef,
        (snap) => setter(snap.docs.map((d) => ({ id: d.id, path: d.ref.path, ...d.data() }))),
        (err) => {
          console.warn(`Cannot load ${name}`, err);
          setErrors((prev) => [...prev.filter((e) => e.name !== name), { name, message: err?.message || String(err) }]);
        }
      );

    const subs = [];

    subs.push(
      makeSub(
        "class_activities",
        query(collection(db, "class_activities"), where("classId", "==", classInfo.id), orderBy("createdAt", "desc"), limit(80)),
        setManualLogs
      )
    );

    subs.push(
      makeSub(
        "assignments",
        query(collection(db, "assignments"), where("classId", "==", classInfo.id), orderBy("createdAt", "desc"), limit(60)),
        setAssignments
      )
    );

    subs.push(
      makeSub(
        "class_notifications",
        query(collection(db, "class_notifications"), where("classId", "==", classInfo.id), orderBy("createdAt", "desc"), limit(60)),
        setNotifications
      )
    );

    subs.push(
      makeSub(
        "attendance",
        query(collection(db, "attendance"), where("classId", "==", classInfo.id), orderBy("updatedAt", "desc"), limit(40)),
        setAttendance
      )
    );

    subs.push(
      makeSub(
        "test_histories",
        query(collection(db, "test_histories"), where("classId", "==", classInfo.id), orderBy("submittedAt", "desc"), limit(80)),
        setHistories
      )
    );

    return () => subs.forEach((u) => u && u());
  }, [classInfo?.id]);

  useEffect(() => {
    if (!notifications.length) {
      setReads([]);
      setErrors((prev) => prev.filter((e) => e.name !== "notification_reads"));
      return undefined;
    }

    const stores = new Map();
    const unsubscribes = notifications.map((item) => {
      const readsRef = query(collection(db, "class_notifications", item.id, "reads"), orderBy("readAt", "desc"));
      return onSnapshot(
        readsRef,
        (snap) => {
          stores.set(
            item.id,
            snap.docs.map((d) => ({
              id: d.id,
              path: d.ref.path,
              notificationId: item.id,
              notificationTitle: item.title,
              classId: item.classId,
              ...d.data(),
            }))
          );
          setReads(Array.from(stores.values()).flat().slice(0, 80));
          setErrors((prev) => prev.filter((e) => e.name !== "notification_reads"));
        },
        (err) => {
          console.warn("Cannot load notification reads", item.id, err);
          setErrors((prev) => [
            ...prev.filter((e) => e.name !== "notification_reads"),
            { name: "notification_reads", message: err?.message || String(err) },
          ]);
        }
      );
    });

    return () => unsubscribes.forEach((u) => u && u());
  }, [notifications]);

  const assignmentMap = useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [assignments]);

  const activities = useMemo(() => {
    const fromManual = manualLogs.map((item) => ({
      id: `manual-${item.id}`,
      source: "class_activities",
      type: item.type || "class_activity",
      severity: item.severity || "normal",
      title: item.targetTitle || item.actorName || "Hoạt động lớp học",
      message: item.message,
      actorName: item.actorName,
      createdAt: item.createdAt,
      metadata: item.metadata || {},
    }));

    const fromAssignments = assignments.map((item) => ({
      id: `assignment-${item.id}`,
      source: "assignments",
      type: "assignment_created",
      severity: item.published === false ? "warning" : "normal",
      title: displayText(item.title, "Bài tập/kiểm tra"),
      message: `${item.published === false ? "Đã tạo bản nháp" : "Đã giao bài"}: ${displayText(item.title, "Bài tập/kiểm tra")}${item.deadline ? ` · hạn ${item.deadline}` : ""}.`,
      createdAt: item.createdAt || item.updatedAt,
      metadata: item,
    }));

    const fromNotifications = notifications.map((item) => ({
      id: `notification-${item.id}`,
      source: "class_notifications",
      type: "notification_created",
      severity: item.published === false ? "warning" : "normal",
      title: item.title || "Thông báo",
      message: `${item.published === false ? "Đã lưu bản nháp thông báo" : "Đã đăng thông báo"}: ${item.title || "Thông báo"}.`,
      createdAt: item.createdAt || item.updatedAt,
      metadata: item,
    }));

    const fromAttendance = attendance.map((item) => ({
      id: `attendance-${item.id || item.date}`,
      source: "attendance",
      type: "attendance_updated",
      severity: "normal",
      title: `Điểm danh ${item.date || ""}`,
      message: buildAttendanceMessage(item),
      createdAt: item.updatedAt || item.createdAt || item.date,
      metadata: item,
    }));

    const submitted = histories.filter((h) => {
      const st = (h.status || "").toLowerCase();
      return h.submittedAt || st === "submitted" || st === "finished";
    });

    const fromHistories = submitted.map((item) => {
      const studentId = item.userId || item.studentId || item.uid;
      const studentName = item.userName || item.studentName || getStudentName(studentMap, studentId);
      const assignmentTitle = item.assignmentTitle || item.testTitle || displayText(assignmentMap[item.assignmentId]?.title, "bài kiểm tra");
      const percent = getPercent(item);
      const low = typeof percent === "number" && percent < 50;
      return {
        id: `history-${item.id}`,
        source: "test_histories",
        type: "assignment_submitted",
        severity: low ? "warning" : "normal",
        title: assignmentTitle,
        message: `${studentName} đã nộp ${assignmentTitle}${typeof percent === "number" ? ` · ${percent}%` : ""}.`,
        actorName: studentName,
        createdAt: item.submittedAt || item.updatedAt || item.createdAt,
        metadata: item,
      };
    });

    const fromReads = reads.map((item) => ({
      id: `read-${item.path || `${item.notificationId || "unknown"}-${item.userId || item.id}`}`,
      source: "notification_reads",
      type: "notification_read",
      severity: "normal",
      title: item.notificationTitle || "Thông báo",
      message: `${item.studentName || getStudentName(studentMap, item.userId)} đã đọc thông báo${item.notificationTitle ? `: ${item.notificationTitle}` : ""}.`,
      actorName: item.studentName,
      createdAt: item.readAt,
      metadata: item,
    }));

    const merged = [
      ...fromManual,
      ...fromAssignments,
      ...fromNotifications,
      ...fromAttendance,
      ...fromHistories,
      ...fromReads,
    ];

    const seen = new Set();
    return merged
      .filter((item) => {
        const key = `${item.type}-${item.metadata?.targetId || item.metadata?.assignmentId || item.metadata?.notificationId || item.metadata?.id || item.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
  }, [manualLogs, assignments, notifications, attendance, histories, reads, assignmentMap, studentMap]);

  const stats = useMemo(() => {
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      total: activities.length,
      thisWeek: activities.filter((a) => tsMs(a.createdAt) >= since).length,
      submissions: activities.filter((a) => a.type === "assignment_submitted").length,
      warnings: activities.filter((a) => a.severity === "warning" || a.severity === "danger").length,
    };
  }, [activities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activities.filter((item) => {
      const okType = typeFilter === "all" || item.type === typeFilter;
      const okSearch = !q || `${item.title || ""} ${item.message || ""} ${item.actorName || ""}`.toLowerCase().includes(q);
      return okType && okSearch;
    });
  }, [activities, search, typeFilter]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <FiActivity className="text-blue-600" /> Hoạt động lớp học
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Theo dõi realtime các thay đổi quan trọng: điểm danh, bài tập, nộp bài, thông báo và cập nhật học viên.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-blue-700">{stats.total}</div>
          <div className="text-xs text-slate-500">Tổng hoạt động</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-emerald-600">{stats.thisWeek}</div>
          <div className="text-xs text-slate-500">7 ngày gần đây</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-purple-600">{stats.submissions}</div>
          <div className="text-xs text-slate-500">Lần nộp bài</div>
        </div>
        <div className="rounded-xl border bg-white p-4 text-center">
          <div className="text-xl font-bold text-amber-600">{stats.warnings}</div>
          <div className="text-xs text-slate-500">Cần chú ý</div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          Một số nguồn dữ liệu chưa tải được. Nếu Firebase yêu cầu index, bấm link trong console để tạo index.
        </div>
      )}

      <div className="rounded-2xl border bg-white p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border bg-white py-2 pl-9 pr-3 outline-none focus:ring-2 focus:ring-blue-100"
              placeholder="Tìm hoạt động theo tên học viên, bài tập, nội dung..."
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-xl border bg-white px-3 py-2"
          >
            <option value="all">Tất cả hoạt động</option>
            <option value="student_enrolled">Thêm học viên</option>
            <option value="attendance_updated">Điểm danh</option>
            <option value="assignment_created">Giao bài</option>
            <option value="assignment_updated">Cập nhật bài</option>
            <option value="assignment_submitted">Nộp bài</option>
            <option value="notification_created">Thông báo</option>
            <option value="notification_read">Đọc thông báo</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">
            Chưa có hoạt động phù hợp.
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-5 top-2 bottom-2 w-px bg-slate-200" />
            <div className="space-y-3">
              {filtered.map((item) => {
                const meta = typeMeta[item.type] || typeMeta.class_activity;
                return (
                  <article key={item.id} className="relative flex gap-3 rounded-2xl border bg-white p-4 shadow-sm hover:shadow-md transition">
                    <ActivityIcon type={item.type} severity={item.severity} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          {meta.label}
                        </span>
                        {item.severity === "warning" && (
                          <span className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            Cần chú ý
                          </span>
                        )}
                        {item.severity === "danger" && (
                          <span className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                            Khẩn cấp
                          </span>
                        )}
                      </div>
                      <h3 className="mt-2 font-bold text-slate-900 line-clamp-1">{item.title || meta.label}</h3>
                      <p className="mt-1 text-sm text-slate-700">{item.message}</p>
                      <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                        <FiClock /> {fmt(item.createdAt)}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
